import * as api from './customerGroupsApi';
import { ConfirmationModal, ModalProvider, useModal } from '../generalcomponents/ModalProvider';
import { DetailViewShell, FormRow, sx13 } from '../common/detailView';
import { TextField, Typography } from '@mui/material';
import { useCallback, useEffect } from 'react';
import type { CustomerGroup } from './customerGroupsApi';
import { useDetailView } from '../common/useDetailView';
import { useTranslation } from 'react-i18next';

interface Props {
    customerGroupId: number | null;
}

export function CustomerGroupView(props: Props) {
    return (
        <ModalProvider>
            <CustomerGroupViewInner {...props} />
        </ModalProvider>
    );
}

function CustomerGroupViewInner({ customerGroupId }: Props) {
    const { t } = useTranslation();
    const { openModal } = useModal();
    const detail = useDetailView<CustomerGroup>();

    const load = useCallback(async () => {
        await detail.withLoading(async () => {
            if (customerGroupId === null) {
                detail.setItem({ id: 0, code: '', name: '' });
                detail.setIsNew(true);
                detail.setIsEditing(true);
            } else {
                const data = await api.getCustomerGroup(customerGroupId);
                detail.setItem(data);
                detail.setIsNew(false);
                detail.setIsEditing(false);
            }
        });
    }, [customerGroupId]);

    useEffect(() => { load(); }, [load]);

    const isCodeValid = (): boolean => !!detail.item?.code && detail.item.code.length <= 20;

    const save = async () => {
        await detail.withSaving(async () => {
            const response = detail.isNew ?
                await api.createCustomerGroup(detail.item!) :
                await api.updateCustomerGroup(detail.item!);
            detail.setItem(response);
            detail.setIsNew(false);
            detail.setIsEditing(false);
        });
    };

    const del = () => {
        openModal(
            <ConfirmationModal
                header={t('common.delete')}
                message={t('customergroup.confirmDelete')}
                onModalResponse={(confirmed) => { if (confirmed) performDelete(); }}
            />
        );
    };

    const performDelete = async () => {
        await detail.withSaving(async () => {
            await api.deleteCustomerGroup(detail.item!.id);
            window.history.back();
        });
    };

    return (
        <DetailViewShell<CustomerGroup>
            featureKey="devFeature_reactCustomerGroupView"
            title={`${detail.item?.code ?? ''} ${detail.item?.name ?? ''}`}
            isEditing={detail.isEditing}
            isNew={detail.isNew}
            saving={detail.saving}
            canSave={isCodeValid()}
            loading={detail.loading}
            error={detail.error}
            item={detail.item}
            onEdit={detail.startEdit}
            onSave={save}
            onCancel={detail.cancelEdit}
            onDelete={del}
            onClearError={() => detail.setError(null)}
        >
            {(item) => (
                <>
                    <FormRow label={t('customergroup.code')}>
                        {!detail.isNew && !detail.isEditing ? (
                            <Typography sx={sx13}>{item.code}</Typography>
                        ) : (
                            <TextField size="small" fullWidth
                                inputProps={{ maxLength: 20, style: { textTransform: 'uppercase' } }}
                                value={item.code}
                                onChange={(e) => detail.update('code', e.target.value.toUpperCase())}
                                error={!isCodeValid()} />
                        )}
                    </FormRow>

                    <FormRow label={t('customergroup.name')}>
                        {!detail.isEditing ? (
                            <Typography sx={sx13}>{item.name}</Typography>
                        ) : (
                            <TextField size="small" fullWidth inputProps={{ maxLength: 100 }}
                                value={item.name}
                                onChange={(e) => detail.update('name', e.target.value)} />
                        )}
                    </FormRow>
                </>
            )}
        </DetailViewShell>
    );
}
